import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../../context/LanguageContext";

const Txt = ({ text }) =>
  text.split("*").map((seg, i) =>
    i % 2 === 1 ? (
      <em key={i} className="font-serif italic text-[#C5A059]">{seg}</em>
    ) : (
      <span key={i}>{seg}</span>
    )
  );

// Rueda de luz que se materializa girando mientras carga (0→100%).
const SPOKES = 24;
const R = 78;
const CIRC = 2 * Math.PI * R;

const LoaderWheel = ({ progress }) => {
  const p = progress / 100;
  const shown = Math.round(p * SPOKES);
  return (
    <div className="relative w-64 h-64 md:w-[420px] md:h-[420px] lg:w-[500px] lg:h-[500px]">
      {/* glow dorado difuso */}
      <div
        className="absolute inset-0 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(197,160,89,0.22), transparent 62%)", opacity: 0.4 + p * 0.6 }}
      />

      {/* anillo exterior punteado (contra-giro) */}
      <motion.svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" fill="none"
        animate={{ rotate: -360 }} transition={{ duration: 18, repeat: Infinity, ease: "linear" }}>
        <circle cx="100" cy="100" r="94" stroke="#C5A059" strokeOpacity="0.12" strokeWidth="1" strokeDasharray="2 10" />
      </motion.svg>

      {/* rueda: radios + llanta girando */}
      <motion.svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" fill="none"
        animate={{ rotate: 360 }} transition={{ duration: 3.4, repeat: Infinity, ease: "linear" }}>
        <circle cx="100" cy="100" r={R} stroke="#C5A059" strokeOpacity="0.12" strokeWidth="1.5" />
        <circle
          cx="100" cy="100" r={R} stroke="#C5A059" strokeWidth="2" strokeLinecap="round"
          strokeDasharray={CIRC} strokeDashoffset={CIRC * (1 - p)}
          transform="rotate(-90 100 100)"
          style={{ filter: "drop-shadow(0 0 6px rgba(197,160,89,0.65))" }}
        />
        {Array.from({ length: SPOKES }).map((_, i) => {
          const a = (i / SPOKES) * Math.PI * 2;
          return (
            <line
              key={i}
              x1="100" y1="100"
              x2={100 + R * Math.cos(a)} y2={100 + R * Math.sin(a)}
              stroke="#C5A059" strokeWidth="0.75"
              strokeOpacity={i < shown ? 0.5 : 0}
              style={{ transition: "stroke-opacity 0.25s ease" }}
            />
          );
        })}
        <circle cx="100" cy="100" r="8" stroke="#C5A059" strokeOpacity="0.5" strokeWidth="1" />
        <circle cx="100" cy="100" r="3.5" fill="#C5A059" />
      </motion.svg>

      {/* núcleo pulsante */}
      <motion.div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#C5A059]"
        style={{ width: 9, height: 9, boxShadow: "0 0 16px #C5A059" }}
        animate={{ scale: [1, 1.9, 1], opacity: [0.55, 1, 0.55] }}
        transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
      />
    </div>
  );
};

export const IntroLoader = () => {
  const { t } = useLang();
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const start = performance.now();
    const dur = 2000;
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      setProgress(Math.round(p * 100));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setTimeout(() => setDone(true), 400);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    if (done) document.body.style.overflow = "";
  }, [done]);

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          data-testid="intro-loader"
          exit={{ y: "-100%" }}
          transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
          className="fixed inset-0 z-[100] bg-[#050505] flex flex-col justify-between p-8 md:p-14"
        >
          <motion.div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <LoaderWheel progress={progress} />
          </motion.div>

          <div className="relative font-serif text-3xl md:text-6xl leading-tight text-white">
            <p><Txt text={t.loader.top1} /></p>
            <p><Txt text={t.loader.top2} /></p>
          </div>
          <div className="flex items-end justify-between">
            <span data-testid="loader-progress" className="font-serif italic text-2xl md:text-4xl text-[#C5A059]">
              {progress}%
            </span>
            <div className="font-serif text-3xl md:text-6xl leading-tight text-white text-right">
              <p><Txt text={t.loader.bottom1} /></p>
              <p><Txt text={t.loader.bottom2} /></p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
